import React, { useState, useEffect } from "react";
import { Form, Button, Container, Alert, Spinner, Card } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { useEditMode } from "../../Context/EditModeContext";
import { useTranslation } from "react-i18next"; // Importer useTranslation

function UploadLesson({ lessonId, setLesson, lesson }) {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [program, setProgram] = useState(null);
  const { id } = useParams();
  const [successMessage, setSuccessMessage] = useState("");
  const { authData } = useAuth();
  const { isEditMode } = useEditMode();

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/programs/${id}`
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || t("error_occurred"));
        }

        setProgram(data.program);
      } catch (err) {
        setError(err.message);
        console.error(err);
      }
    };

    fetchProgram();
  }, [id, t]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    setError("");
    setSuccessMessage("");
    e.preventDefault();
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/lessons/lesson/${lesson._id}/upload-file`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || t("error_occurred"));
        }

        setSuccessMessage(data.message);
        setLesson(data.lesson);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        console.error(err);
        setIsLoading(false);
      }
    }
  };

  const handleDownloadFile = async () => {
    try {
      if (lesson && lesson.content) {
        let fileName = "document";

        if (lesson.fileName) {
          fileName = lesson.fileName;
        } else {
          const urlParts = lesson.content.split("/");
          const defaultFileName = urlParts[urlParts.length - 1];

          const extension = defaultFileName.includes(".")
            ? defaultFileName.split(".").pop()
            : "";

          fileName = `document${extension ? "." + extension : ""}`;
        }

        const response = await fetch(lesson.content);

        if (!response.ok) {
          throw new Error(t("download_error_file"));
        }

        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        throw new Error(t("no_file_available"));
      }
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  if (!program) {
    return <div>{t("loading_program")}</div>;
  }

  if (!lesson) {
    return <div>{t("loading_lesson")}</div>;
  }

  if (lesson.content !== "Non Defini") {
    return (
      <>
        {isEditMode && program.created_by._id === authData.userId ? (
          <Container className="p-5 text-center mt-3">
            <h2 className="mb-4">{t("edit_file")}</h2>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="formFile" className="mb-3">
                <Form.Label className="fw-bold">
                  {t("choose_or_drop_file")}
                </Form.Label>
                <Form.Control type="file" onChange={handleFileChange} />
              </Form.Group>
              <Button variant="primary" type="submit">
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  t("upload_file")
                )}
              </Button>
            </Form>
            {file && (
              <Alert variant="info" className="mt-3">
                {t("selected_file")}: {file.name}
              </Alert>
            )}
            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}
            {successMessage !== "" && (
              <Alert variant="success" className="mt-3">
                {successMessage}
              </Alert>
            )}
          </Container>
        ) : (
          <Container className="text-center">
            <Card className="mt-3">
              <Card.Body>
                <Card.Title>{t("downloadable_file")}</Card.Title>
                <Card.Text>{t("click_to_download_file")}</Card.Text>
                <Button variant="primary" onClick={handleDownloadFile}>
                  {t("download_file")}
                </Button>
              </Card.Body>
            </Card>
          </Container>
        )}
      </>
    );
  }

  if (isEditMode && program.created_by._id === authData.userId) {
    return (
      <Container className="p-5 text-center mt-3">
        <h2 className="mb-4">{t("add_file")}</h2>
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="formFile" className="mb-3">
            <Form.Label className="fw-bold">
              {t("choose_or_drop_file")}
            </Form.Label>
            <Form.Control type="file" onChange={handleFileChange} />
          </Form.Group>
          <Button variant="primary" type="submit">
            {isLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              t("upload_file")
            )}
          </Button>
        </Form>
        {file && (
          <Alert variant="info" className="mt-3">
            {t("selected_file")}: {file.name}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
        {successMessage !== "" && (
          <Alert variant="success" className="mt-3">
            {successMessage}
          </Alert>
        )}
      </Container>
    );
  }

  return (
    <Container className="p-5 border rounded bg-light text-center">
      <p>{t("lesson_empty")}</p>
    </Container>
  );
}

export default UploadLesson;
